/**
 * Тумблер «Уведомления на это устройство» (план 110).
 *
 * ЗАЧЕМ. Колокольчик узнаёт о новом, только пока открыта вкладка. Push
 * доставляет заявку на привязку и сообщение клиента в закрытое приложение.
 *
 * ГДЕ ЖИВЁТ. В «Моём профиле», а не в разделе «Настройки»: тот закрыт правом
 * `integration:errors_view` (боссу и управляющему), а пуши нужнее всего
 * администратору на ресепшене и педагогу.
 *
 * «ЭТО УСТРОЙСТВО» — не фигура речи: подписка принадлежит браузеру. Включив
 * пуши на телефоне, человек не включает их на рабочем компьютере, и наоборот;
 * поэтому состояние тумблера читается с сервера по endpoint ЭТОГО браузера.
 *
 * iOS. Safari разрешает push только из приложения, установленного на экран
 * «Домой» (16.4+). Во вкладке `pushManager` просто нет — вместо неработающего
 * тумблера показываем, что делать.
 */
import { useCallback, useEffect, useState } from 'react';

import { api } from '@/lib/api';
import { isStandalone, isIOS } from '@/lib/pwa';

interface StatusResponse {
  subscribed: boolean;
}

/**
 * base64url → ArrayBuffer: в этом виде `pushManager.subscribe` принимает ключ
 * VAPID. Возвращаем именно буфер, а не Uint8Array: типы DOM ждут
 * `BufferSource`, и представление поверх SharedArrayBuffer им не подходит.
 */
function urlBase64ToBuffer(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}

export function PushToggle(): JSX.Element | null {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vapidKey, setVapidKey] = useState<string | null>(null);

  // Поддержка + текущее состояние подписки ЭТОГО браузера.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const hasApi =
        typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        typeof window !== 'undefined' &&
        'PushManager' in window &&
        'Notification' in window;
      if (!hasApi) return;

      try {
        const { key } = await api.get<{ key: string }>('/web-push/vapid-key');
        if (cancelled) return;
        // Пустой ключ = пуши в этом окружении не настроены. Показывать тумблер,
        // который всегда падает, хуже, чем не показывать его вовсе.
        if (!key) return;
        setVapidKey(key);
        setSupported(true);

        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (cancelled || !existing) return;
        const status = await api.get<StatusResponse>(
          `/web-push/status?endpoint=${encodeURIComponent(existing.endpoint)}`,
        );
        if (!cancelled) setSubscribed(status.subscribed);
      } catch {
        // Тихо: неработающий тумблер лучше не показывать, чем показать с ошибкой.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError(
          permission === 'denied'
            ? 'Уведомления запрещены в настройках браузера — разрешите их для этого сайта.'
            : 'Разрешение не получено.',
        );
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToBuffer(vapidKey ?? ''),
      });
      const json = sub.toJSON();
      await api.post('/web-push', {
        endpoint: sub.endpoint,
        p256dh: json.keys?.['p256dh'] ?? '',
        auth: json.keys?.['auth'] ?? '',
        user_agent: navigator.userAgent.slice(0, 512),
      });
      setSubscribed(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось включить уведомления');
    } finally {
      setBusy(false);
    }
  }, [vapidKey]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post('/web-push/unsubscribe', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось выключить уведомления');
    } finally {
      setBusy(false);
    }
  }, []);

  // iOS вне установленного приложения: тумблер там физически не сработает —
  // объясняем, что сделать, вместо мёртвой кнопки.
  if (!supported && isIOS() && !isStandalone()) {
    return (
      <div className="rounded-card border border-divider-gray p-4">
        <div className="text-sm font-medium text-ink-black">Уведомления на телефон</div>
        <p className="mt-1 text-sm text-muted-text">
          На iPhone уведомления приходят только в приложение, добавленное на экран «Домой». Нажмите
          «Поделиться» → «На экран „Домой“», откройте CRM с ярлыка и включите уведомления там.
        </p>
      </div>
    );
  }

  if (!supported) return null;

  return (
    <div className="rounded-card border border-divider-gray p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink-black">Уведомления на это устройство</div>
          <p className="mt-1 text-sm text-muted-text">
            Заявки и новые сообщения будут приходить, даже когда CRM закрыта. Настройка касается
            только этого браузера.
          </p>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => void (subscribed ? disable() : enable())}
          className={
            subscribed
              ? 'shrink-0 rounded-full border border-divider-gray px-4 py-2 text-sm text-muted-text hover:bg-whisper-white'
              : 'shrink-0 rounded-full bg-blueprint-blue px-4 py-2 text-sm text-canvas-white'
          }
        >
          {busy ? '…' : subscribed ? 'Выключить' : 'Включить'}
        </button>
      </div>
      {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
